import React from "react";
import { ConversationDto, ConversationSummaryDto, useSmartsuppVisitorInfo } from "../../../api/hooks/useSmartsupp";
import StatusPill, { statusLabel } from "./StatusPill";
import AgentBadge from "./AgentBadge";
import { Star } from "lucide-react";
import { countryCodeToFlag } from "./utils/countryCodeToFlag";
import Section from "./Section";
import ShoptetCustomerCard from "./ShoptetCustomerCard";
import VisitorInfoCard from "./VisitorInfoCard";

interface ContactDetailsPanelProps {
  conversation: ConversationDto;
  onSelectConversation?: (id: string) => void;
}

const SHOPTET_KEYS = new Set(["shoptet_guid", "shoptet_shop", "shoptet_user_guid", "shoptet_cart_updated_at"]);

function mergedInfoEntries(
  variables: Record<string, string>,
  contactProperties: Record<string, string>
): [string, string][] {
  // variables win on key collision — they reflect live conversation state, contactProperties are static contact defaults
  const merged: Record<string, string> = { ...contactProperties, ...variables };
  const entries = Object.entries(merged);
  const shoptet = entries.filter(([k]) => SHOPTET_KEYS.has(k));
  const rest = entries.filter(([k]) => !SHOPTET_KEYS.has(k));
  return [...shoptet, ...rest];
}

function OtherConversationRow({
  conv,
  onSelect,
}: {
  conv: ConversationSummaryDto;
  onSelect?: (id: string) => void;
}) {
  const date = conv.lastMessageAt
    ? new Date(conv.lastMessageAt).toLocaleDateString("cs-CZ")
    : "—";
  return (
    <button
      type="button"
      data-testid={`other-conversation-${conv.id}`}
      onClick={() => onSelect?.(conv.id ?? "")}
      className="w-full text-left py-1.5 border-b border-gray-50 dark:border-graphite-border last:border-0 rounded hover:bg-gray-50 dark:hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
    >
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-xs text-gray-500 dark:text-graphite-muted">{date}</span>
        <span className="text-xs text-gray-500 dark:text-graphite-muted">{statusLabel(conv.status)}</span>
      </div>
      {conv.lastMessagePreview && (
        <div className="text-xs text-gray-700 dark:text-graphite-muted truncate">{conv.lastMessagePreview}</div>
      )}
    </button>
  );
}

function ContactDetailsPanel({ conversation, onSelectConversation }: ContactDetailsPanelProps) {
  const displayName = conversation.contactName ?? conversation.contactEmail ?? "Neznámý";
  const hasRating = typeof conversation.rating === "number";

  const locationParts = [conversation.locationCity, conversation.locationCountry].filter(Boolean);
  const locationText = locationParts.length ? locationParts.join(", ") : null;
  const flag = conversation.locationCountry ? countryCodeToFlag(conversation.locationCountry) : "";

  const hasKontakt =
    conversation.contactPhone ||
    conversation.locationIp ||
    locationText ||
    conversation.domain ||
    conversation.referer ||
    conversation.channel;

  const infoEntries = mergedInfoEntries(conversation.variables ?? {}, conversation.contactProperties ?? {});

  const { data: visitorData, isLoading: visitorLoading } = useSmartsuppVisitorInfo(conversation.id ?? null);
  const visitorInfo = visitorData?.visitorInfo;

  return (
    <aside className="h-full w-full overflow-y-auto bg-white dark:bg-graphite-surface border-l border-gray-200 dark:border-graphite-border">
      {/* Header */}
      <div className="px-4 py-4 border-b border-gray-200 dark:border-graphite-border flex items-center gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-medium">
          {displayName.slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-sm text-gray-900 dark:text-graphite-text truncate">{displayName}</div>
          {conversation.contactEmail && (
            <div className="text-xs text-gray-500 dark:text-graphite-muted truncate">{conversation.contactEmail}</div>
          )}
          {(visitorLoading || visitorInfo) && (
            <div className="flex gap-3 mt-1">
              <span className="text-xs text-gray-500 dark:text-graphite-muted" data-testid="visits-count">
                {visitorLoading ? (
                  <span className="inline-block w-8 h-2 bg-gray-200 dark:bg-graphite-hover rounded animate-pulse" />
                ) : visitorInfo?.visitsCount != null ? (
                  <>Návštěvy <span className="font-medium text-gray-800 dark:text-graphite-text">{visitorInfo.visitsCount}</span></>
                ) : null}
              </span>
              <span className="text-xs text-gray-500 dark:text-graphite-muted" data-testid="chats-count">
                {visitorLoading ? (
                  <span className="inline-block w-8 h-2 bg-gray-200 dark:bg-graphite-hover rounded animate-pulse" />
                ) : visitorInfo?.chatsCount != null ? (
                  <>Chaty <span className="font-medium text-gray-800 dark:text-graphite-text">{visitorInfo.chatsCount}</span></>
                ) : null}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Status */}
      <Section title="Stav">
        <StatusPill status={conversation.status} />
        {conversation.closeType && (
          <div className="text-xs text-gray-500 dark:text-graphite-muted mt-1.5">Uzavřeno: {conversation.closeType}</div>
        )}
      </Section>

      {/* Rating */}
      {hasRating && (
        <Section title="Hodnocení">
          <div data-testid="rating" className="flex items-center gap-1 text-amber-500">
            <Star className="w-4 h-4 fill-amber-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-graphite-text">{conversation.rating}</span>
            <span className="text-xs text-gray-400 dark:text-graphite-faint">/ 5</span>
          </div>
          {conversation.ratingText && (
            <p className="text-xs text-gray-600 dark:text-graphite-muted mt-1.5 italic">"{conversation.ratingText}"</p>
          )}
        </Section>
      )}

      {/* Assigned agents */}
      {(conversation.assignedAgentIds ?? []).length > 0 && (
        <Section title="Přiřazení operátoři">
          <div className="flex flex-wrap gap-1.5">
            {(conversation.assignedAgentIds ?? []).map((id) => (
              <AgentBadge key={id} agentId={id} name={id} />
            ))}
          </div>
        </Section>
      )}

      {/* Kontakt — phone, IP, location+flag, channel, domain, referer */}
      {hasKontakt && (
        <Section title="Kontakt">
          <div className="space-y-1">
            {conversation.contactPhone && (
              <div className="text-sm text-gray-700 dark:text-graphite-muted">{conversation.contactPhone}</div>
            )}
            {conversation.locationIp && (
              <div className="text-xs text-gray-500 dark:text-graphite-muted">{conversation.locationIp}</div>
            )}
            {locationText && (
              <div className="text-sm text-gray-700 dark:text-graphite-muted">
                {flag ? `${flag} ${locationText}` : locationText}
              </div>
            )}
            {conversation.channel && (
              <div className="text-sm text-gray-700 dark:text-graphite-muted">{conversation.channel}</div>
            )}
            {conversation.domain && (
              <div className="text-xs text-gray-500 dark:text-graphite-muted">{conversation.domain}</div>
            )}
            {conversation.referer && (
              <a
                href={conversation.referer}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate block"
              >
                {conversation.referer}
              </a>
            )}
          </div>
        </Section>
      )}

      {/* Note */}
      {conversation.contactNote && (
        <Section title="Poznámka">
          <p className="text-sm text-gray-700 dark:text-graphite-muted whitespace-pre-wrap">{conversation.contactNote}</p>
        </Section>
      )}

      {/* Contact tags */}
      {(conversation.contactTags ?? []).length > 0 && (
        <Section title="Štítky kontaktu">
          <div className="flex flex-wrap gap-1.5">
            {(conversation.contactTags ?? []).map((t) => (
              <span
                key={t}
                className="inline-flex items-center rounded-full px-2 py-0.5 text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
              >
                {t}
              </span>
            ))}
          </div>
        </Section>
      )}

      {/* Conversation tags */}
      {(conversation.tags ?? []).length > 0 && (
        <Section title="Štítky">
          <div className="flex flex-wrap gap-1.5">
            {(conversation.tags ?? []).map((t) => (
              <span
                key={t}
                className="inline-flex items-center rounded-full px-2 py-0.5 text-xs bg-gray-100 dark:bg-graphite-surface-2 text-gray-700 dark:text-graphite-muted"
              >
                {t}
              </span>
            ))}
          </div>
        </Section>
      )}

      {/* Other conversations */}
      {(conversation.otherConversations ?? []).length > 0 && (
        <Section title={`Jiné konverzace (${(conversation.otherConversations ?? []).length})`}>
          {(conversation.otherConversations ?? []).map((c) => (
            <OtherConversationRow key={c.id} conv={c} onSelect={onSelectConversation} />
          ))}
        </Section>
      )}

      {/* Visitor info — OS, browser, browsing history */}
      <VisitorInfoCard conversationId={conversation.id ?? null} />

      {/* Shoptet Zákazník — rendered when resolved */}
      <ShoptetCustomerCard conversationId={conversation.id ?? null} />

      {/* Informace o kontaktu — merged variables + contactProperties, Shoptet keys first */}
      {infoEntries.length > 0 && (
        <Section title="Informace o kontaktu">
          <div className="space-y-1">
            {infoEntries.map(([key, value]) => (
              <div key={key} className="flex gap-2 text-xs">
                <span className="text-gray-500 dark:text-graphite-muted shrink-0 w-1/2 truncate">{key}</span>
                <span className="text-gray-800 dark:text-graphite-text truncate">{value}</span>
              </div>
            ))}
          </div>
        </Section>
      )}
    </aside>
  );
}

export default ContactDetailsPanel;
